
import { TradeProfile } from "@/types/Campaign";
import { CorporateAcceptanceProfile } from "@/types/AcceptanceProfile";
import {
  SPEND_BUCKETS,
  TRANSACTION_TYPES,
  BucketSummary,
  TransactionTypeAnalysis,
  PaymentMethodBreakdown,
  categorizeSuppliersBySpend,
} from "./constants-and-types";

// Generate spend bucket summary from trade profiles
/** Generate spend bucket summary. */
export function generateSpendBucketSummary(tradeProfiles: TradeProfile[]): BucketSummary[] {
  const bucketMap = new Map<string, { recordCount: number; totalSpend: number; enrollmentRate: number }>();

  Object.values(SPEND_BUCKETS).forEach(bucket => {
    bucketMap.set(bucket.label, { recordCount: 0, totalSpend: 0, enrollmentRate: bucket.enrollmentRate });
  });

  tradeProfiles.forEach(profile => {
    const spend = profile.annualVolume || profile.spendVolume || 0;
    const bucket = categorizeSuppliersBySpend(spend);
    const bucketData = bucketMap.get(bucket);
    if (bucketData) {
      bucketData.recordCount++;
      bucketData.totalSpend += spend;
    }
  });

  const summaries: BucketSummary[] = [];
  let totalSuppliers = 0;
  let totalSpend = 0;
  let totalProjectedSpend = 0;
  let totalProjectedSuppliers = 0;

  bucketMap.forEach((data, bucket) => {
    const supplierCount = data.recordCount;
    const projectedSpendEnrollment = data.totalSpend * (data.enrollmentRate / 100);
    const projectedSupplierEnrollment = supplierCount * (data.enrollmentRate / 100);
    summaries.push({
      bucket,
      supplierCount,
      totalSpend: data.totalSpend,
      spendMigrationPct: data.enrollmentRate,
      supplierEnrollmentPct: data.enrollmentRate,
      projectedSpendEnrollment
    });
    totalSuppliers += supplierCount;
    totalSpend += data.totalSpend;
    totalProjectedSpend += projectedSpendEnrollment;
    totalProjectedSuppliers += projectedSupplierEnrollment;
  });

  const weightedSpendMigrationPct = totalSpend > 0 ? Math.min(100, (totalProjectedSpend / totalSpend) * 100) : 0;
  const supplierEnrollmentPct = totalSuppliers > 0 ? Math.min(100, (totalProjectedSuppliers / totalSuppliers) * 100) : 0;

  summaries.push({
    bucket: 'Total',
    supplierCount: totalSuppliers,
    totalSpend,
    spendMigrationPct: Math.round(weightedSpendMigrationPct * 100) / 100,
    supplierEnrollmentPct: Math.round(supplierEnrollmentPct * 100) / 100,
    projectedSpendEnrollment: totalProjectedSpend
  });

  return summaries;
}

// Analyze payment methods from trade profiles
/** Analyze payment methods. */
export function analyzePaymentMethods(tradeProfiles: TradeProfile[], _cardEligibleOnly: boolean = false): PaymentMethodBreakdown[] {
  const methodMap = new Map<string, { recordCount: number; totalSpend: number; transactionCount: number }>();

  tradeProfiles.forEach(profile => {
    const method = profile.currentPaymentMethod || 'Unknown';
    const spend = profile.annualVolume || profile.spendVolume || 0;
    const transactions = profile.transactionCount || 0;
    if (!methodMap.has(method)) {
      methodMap.set(method, { recordCount: 0, totalSpend: 0, transactionCount: 0 });
    }
    const methodData = methodMap.get(method) ?? { recordCount: 0, totalSpend: 0, transactionCount: 0 };
    methodData.recordCount++;
    methodData.totalSpend += spend;
    methodData.transactionCount += transactions;
  });

  const totalSpend = Array.from(methodMap.values()).reduce((sum, data) => sum + data.totalSpend, 0);
  const breakdown: PaymentMethodBreakdown[] = [];
  methodMap.forEach((data, method) => {
    breakdown.push({
      paymentMethod: method,
      suppliers: data.recordCount,
      totalSpend: data.totalSpend,
      percentage: totalSpend > 0 ? (data.totalSpend / totalSpend) * 100 : 0,
      estimatedTransactionCount: data.transactionCount
    });
  });
  breakdown.sort((a, b) => b.totalSpend - a.totalSpend);

  const totalTransactions = Array.from(methodMap.values()).reduce((sum, data) => sum + data.transactionCount, 0);
  breakdown.push({
    paymentMethod: 'Total',
    suppliers: tradeProfiles.length,
    totalSpend,
    percentage: 100,
    estimatedTransactionCount: totalTransactions
  });

  return breakdown;
}

// Analyze transaction types (Virtual Card Analysis)
/** Analyze transaction types. */
export function analyzeTransactionTypes(tradeProfiles: TradeProfile[], matchedData?: { matchedCount: number; matchedSpend: number }): TransactionTypeAnalysis[] {
  const totalSpend = tradeProfiles.reduce((sum, p) => sum + (p.annualVolume || p.spendVolume || 0), 0);
  const analysis: TransactionTypeAnalysis[] = [];

  Object.entries(TRANSACTION_TYPES).forEach(([key, config]) => {
    let suppliers = 0;
    let typeSpend = 0;

    if (key === 'TOTAL_IN_NETWORK') {
      suppliers = tradeProfiles.length;
      typeSpend = totalSpend;
    } else if (key === 'LEVEL_III') {
      const filtered = tradeProfiles.filter(p => (p.annualVolume || 0) > 50000);
      suppliers = filtered.length;
      typeSpend = filtered.reduce((sum, p) => sum + (p.annualVolume || 0), 0);
    } else if (key === 'LEVEL_II') {
      const filtered = tradeProfiles.filter(p => (p.annualVolume || 0) > 10000);
      suppliers = filtered.length;
      typeSpend = filtered.reduce((sum, p) => sum + (p.annualVolume || 0), 0);
    } else if (key === 'SMALL_ANNUAL') {
      const filtered = tradeProfiles.filter(p => (p.annualVolume || 0) < 10000);
      suppliers = filtered.length;
      typeSpend = filtered.reduce((sum, p) => sum + (p.annualVolume || 0), 0);
    } else if (key === 'MATCHED') {
      if (matchedData) {
        suppliers = matchedData.matchedCount;
        typeSpend = matchedData.matchedSpend;
      } else {
        suppliers = Math.floor(tradeProfiles.length * 0.7);
        typeSpend = totalSpend * 0.7;
      }
    } else if (key === 'UNMATCHED') {
      if (matchedData) {
        suppliers = tradeProfiles.length - matchedData.matchedCount;
        typeSpend = totalSpend - matchedData.matchedSpend;
      } else {
        suppliers = Math.floor(tradeProfiles.length * 0.3);
        typeSpend = totalSpend * 0.3;
      }
    }

    const projectedCardSpend = typeSpend * (config.enrollmentRate / 100);
    analysis.push({
      transactionType: config.label,
      suppliers,
      totalSpend: typeSpend,
      projectedCardSpend,
      spendMigrationPct: config.enrollmentRate,
      supplierEnrollmentPct: config.enrollmentRate,
      percentOfCardProjection: totalSpend > 0 ? (projectedCardSpend / totalSpend) * 100 : 0,
      percentOfTotalSpend: totalSpend > 0 ? (typeSpend / totalSpend) * 100 : 0
    });
  });

  return analysis.sort((a, b) => b.projectedCardSpend - a.projectedCardSpend);
}

// ─── Portfolio Power Curve (80/20 Fractal Analysis) ───

export interface PowerCurveLayer {
  layer: string;
  theorySpendPct: number;
  theorySupplierPct: number;
  actualSpendPct: number;
  actualSupplierPct: number;
  supplierCount: number;
  spend: number;
  /**
   * MEASURED concentration for THIS row: `actualSpendPct / actualSupplierPct`. A tier holding
   * 18.2% of spend across 0.3% of suppliers is 60.7, not 1000.
   *
   * It was previously a hard-coded theory constant ({16, 64, 250, 1000}) attached to rows whose
   * every other number came from real data — one column, two provenances. The constants did not
   * even agree with the theory columns on their own row: `80/20¹` carries theorySpendPct 80 over
   * theorySupplierPct 20, a 4:1 theoretical ratio, and printed 16:1. (The array was shifted one
   * layer deep: 16 = 64/4, the ratio of the NEXT layer down.) Read as theory it was wrong, and
   * read as measurement — which is what the UI tooltip claims it is — it was wrong by up to 16x.
   * Derive it; never reintroduce a literal here.
   */
  leverageRatio: number;
}

/**
 * Concentration ratio for one power-curve tier: what share of spend it holds per unit of
 * supplier-count share. 1.0 means the tier is exactly proportional; above 1.0 means spend is
 * concentrated in it; below 1.0 means it is long-tail.
 *
 * Both arguments must be percentages of the SAME portfolio and from the SAME row — mixing a
 * disjoint tier's spend share with a cumulative layer's supplier share is the bug this function
 * exists to make impossible to write by accident.
 */
export function computeLeverageRatio(actualSpendPct: number, actualSupplierPct: number): number {
  if (!Number.isFinite(actualSupplierPct) || actualSupplierPct <= 0) return 0;
  if (!Number.isFinite(actualSpendPct)) return 0;
  return actualSpendPct / actualSupplierPct;
}

/**
 * Compute 80/20 fractal layers from sorted supplier spend data.
 * Each layer zooms in: 80/20, 64/4, 52/0.8, etc.
 *
 * Layers are CUMULATIVE (each is a top-N slice that contains the deeper ones). For the
 * non-overlapping tiers a table or cohort model should show, use
 * `deriveDisjointPowerCurveLayers`.
 */
export function analyzePortfolioPowerCurve(
  suppliers: { spend: number }[]
): PowerCurveLayer[] {
  const sorted = [...suppliers].filter(s => s.spend > 0).sort((a, b) => b.spend - a.spend);
  const totalSpend = sorted.reduce((s, d) => s + d.spend, 0);
  const totalCount = sorted.length;
  if (totalCount === 0 || totalSpend === 0) return [];

  const fractalLayers = [
    { layer: "80/20¹", theorySpend: 80, theorySupplier: 20 },
    { layer: "80/20²", theorySpend: 64, theorySupplier: 4 },
    { layer: "80/20³", theorySpend: 51.2, theorySupplier: 0.8 },
    { layer: "80/20⁴", theorySpend: 41, theorySupplier: 0.16 },
  ];

  return fractalLayers
    .map(({ layer, theorySpend, theorySupplier }) => {
      const supplierCutoff = Math.max(1, Math.round(totalCount * (theorySupplier / 100)));
      const topSlice = sorted.slice(0, supplierCutoff);
      const sliceSpend = topSlice.reduce((s, d) => s + d.spend, 0);
      const actualSpendPct = (sliceSpend / totalSpend) * 100;
      const actualSupplierPct = (supplierCutoff / totalCount) * 100;

      return {
        layer,
        theorySpendPct: theorySpend,
        theorySupplierPct: theorySupplier,
        actualSpendPct,
        actualSupplierPct,
        supplierCount: supplierCutoff,
        spend: sliceSpend,
        leverageRatio: computeLeverageRatio(actualSpendPct, actualSupplierPct),
      };
    })
    .filter(l => l.supplierCount >= 1);
}

/**
 * Turn the cumulative fractal layers into the NON-OVERLAPPING tiers a breakdown table or a
 * cohort model needs, plus a "Long Tail" remainder so supplier counts and spend sum to the whole
 * portfolio. Ordered most-concentrated first.
 *
 * Every percentage and the leverage ratio are recomputed against the tier's own exclusive
 * numbers. Callers must not shallow-spread a cumulative layer and patch a subset of its fields:
 * that is precisely how the breakdown table came to print a cumulative layer's leverage next to
 * a disjoint tier's spend and supplier counts.
 */
export function deriveDisjointPowerCurveLayers(
  suppliers: { spend: number }[]
): PowerCurveLayer[] {
  const positive = suppliers.filter(s => s.spend > 0);
  const totalSpend = positive.reduce((s, d) => s + d.spend, 0);
  const totalCount = positive.length;
  const layers = analyzePortfolioPowerCurve(positive);
  if (layers.length === 0 || totalCount === 0 || totalSpend === 0) return [];

  const cumulative = [...layers].reverse(); // most concentrated first
  const tiers: PowerCurveLayer[] = cumulative.map((l, i) => {
    const deeperSpend = i > 0 ? cumulative[i - 1].spend : 0;
    const deeperCount = i > 0 ? cumulative[i - 1].supplierCount : 0;
    const spend = l.spend - deeperSpend;
    const supplierCount = l.supplierCount - deeperCount;
    const actualSpendPct = (spend / totalSpend) * 100;
    const actualSupplierPct = (supplierCount / totalCount) * 100;
    return {
      layer: l.layer,
      theorySpendPct: l.theorySpendPct,
      theorySupplierPct: l.theorySupplierPct,
      actualSpendPct,
      actualSupplierPct,
      supplierCount,
      spend,
      leverageRatio: computeLeverageRatio(actualSpendPct, actualSupplierPct),
    };
  });

  const coveredCount = tiers.reduce((s, t) => s + t.supplierCount, 0);
  const coveredSpend = tiers.reduce((s, t) => s + t.spend, 0);
  const tailCount = totalCount - coveredCount;

  if (tailCount > 0) {
    const tailSpend = totalSpend - coveredSpend;
    const actualSpendPct = (tailSpend / totalSpend) * 100;
    const actualSupplierPct = (tailCount / totalCount) * 100;
    tiers.push({
      layer: "Long Tail",
      theorySpendPct: 0,
      theorySupplierPct: 0,
      actualSpendPct,
      actualSupplierPct,
      supplierCount: tailCount,
      spend: tailSpend,
      leverageRatio: computeLeverageRatio(actualSpendPct, actualSupplierPct),
    });
  }

  return tiers;
}

// ─── Card-to-Account (Plastiq-style) Economics ───
//
// Additive average-cycle model — 10x.md M4 "Approved first-order model" (Do-Now item 3,
// "Card-to-Account correctness"). Card float and existing supplier terms are NOT competing
// clocks: the prior subtractive formula (`max(0, cardCycleDays - avgDaysToPay)`) treated them
// as one, and floored to zero whenever supplier terms met or exceeded the card cycle.
// Supplier terms are surfaced for display only (`supplierTermsDays`) and are never read by
// the float calculation below.

export interface CardToAccountInputs {
  /** Decimal, e.g. 0.027 for 2.7%. */
  buyerFeeRate: number;
  /**
   * Decimal current-tier rebate rate, resolved by the caller via
   * `getRebateRateForTier(cardProgram, cardProgram.current_tier_name)` — NOT a flat/tier-1
   * fallback. `getRebateRateForTier` can return `null` when the tier is unresolved; callers
   * must treat that as a blocking "missing input" condition (render a MissingInputAlert) and
   * not call this function until it resolves to a real number. See
   * `docs/units/R-CE2-REBATE-FALLBACK/plan.md` for why a fabricated fallback here is unsafe.
   */
  currentTierRebateRate: number;
  /** Decimal, e.g. 0.20 for 20%. */
  fedTaxRate: number;
  /** Decimal, e.g. 0.05 for 5%. */
  stateTaxRate: number;
  billingCycleDays: number;
  paymentTermsDays: number;
  /**
   * Existing (pre-C2A) supplier payment terms, in days. Informational/display only —
   * per 10x.md's canonical product decision, "Supplier payment terms remain unchanged" by a
   * C2A conversion, so this value must never be read inside the float calculation below.
   */
  supplierTermsDays?: number | null;
  /** Decimal, e.g. 0.045 for 4.5%. */
  waccRate: number;
}

export interface CardToAccountResult {
  selectedSpend: number;
  supplierCount: number;
  feeCost: number;
  taxBenefit: number;
  rebateValue: number;
  /** `billingCycleDays + paymentTermsDays`, clamped to >= 0. */
  cardCycleDays: number;
  /** `cardCycleDays * 0.5` — the core additive-model fix. Independent of supplier terms. */
  averageFloatDays: number;
  /** Pass-through of the input, unchanged by the calculation. */
  supplierTermsDays: number | null;
  workingCapitalValue: number;
  totalOffsets: number;
  /** Positive = net savings, negative = net cost. */
  netBenefit: number;
  /**
   * Positive = net cost, negative = net savings — `-netBenefit / selectedSpend`.
   * Opposite sign convention from `netBenefit` BY DESIGN (10x.md defines both formulas this
   * way, literally). Do not reuse one sign check for both fields.
   */
  effectiveCostRate: number;
}

/**
 * Calculate Card-to-Account economics using the additive average-cycle model.
 * See 10x.md, "M4. Buyer Tail-Spend Card-to-Account Engine" for the approved formula set.
 */
export function calculateCardToAccountEconomics(
  spend: number,
  supplierCount: number,
  inputs: CardToAccountInputs
): CardToAccountResult {
  const feeCost = spend * inputs.buyerFeeRate;
  const taxBenefit = feeCost * (inputs.fedTaxRate + inputs.stateTaxRate);
  const rebateValue = spend * inputs.currentTierRebateRate;
  // Clamp: a negative or malformed billing_cycle_days/payment_terms_days must never produce
  // a negative float — there is no scenario where a card cycle is < 0 days. Neither column has
  // a DB CHECK constraint or an admin-form minimum today, so this guard is the only floor.
  const cardCycleDays = Math.max(0, inputs.billingCycleDays + inputs.paymentTermsDays);
  const averageFloatDays = cardCycleDays * 0.5;
  const workingCapitalValue = (spend * inputs.waccRate * averageFloatDays) / 365;
  const totalOffsets = rebateValue + workingCapitalValue + taxBenefit;
  const netBenefit = totalOffsets - feeCost;
  const effectiveCostRate = spend > 0 ? (feeCost - totalOffsets) / spend : 0;

  return {
    selectedSpend: spend,
    supplierCount,
    feeCost,
    taxBenefit,
    rebateValue,
    cardCycleDays,
    averageFloatDays,
    supplierTermsDays: inputs.supplierTermsDays ?? null,
    workingCapitalValue,
    totalOffsets,
    netBenefit,
    effectiveCostRate,
  };
}

// ─── Cohort Summaries (Power Curve → Scenario Modeling bridge) ───

import { getCohortName } from "@/constants/cohort-defaults";

export interface CohortSummary {
  cohortName: string;
  supplierCount: number;
  totalSpend: number;
}

/**
 * Extract disjoint cohort summaries from supplier spend data.
 * Reuses the Power Curve fractal logic, mapping each tier to a named cohort.
 */
export function extractCohortSummaries(
  suppliers: { spend: number }[]
): CohortSummary[] {
  const tiers = deriveDisjointPowerCurveLayers(suppliers);
  if (tiers.length === 0) return [];

  return tiers.map((tier, i) => ({
    cohortName: tier.layer === "Long Tail" ? "Long Tail" : getCohortName(i, tiers.length),
    supplierCount: tier.supplierCount,
    totalSpend: tier.spend,
  }));
}
